import { Link } from 'react-router';
import { Briefcase, Heart, Sparkles, Users2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { EnterpriseProofPack } from '../components/enterprise/EnterpriseProofPack';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';
import { useSEO } from '../lib/seo';

const tracks = [
  {
    icon: Heart,
    name: 'Romantic',
    phase: 'Current',
    description: 'Core couples intelligence offering focused on measurable tension-risk trend reduction.',
  },
  {
    icon: Users2,
    name: 'Friendship & family',
    phase: 'Expansion',
    description: 'Expanded scoring and interventions for non-romantic dynamics with distinct risk profiles.',
  },
  {
    icon: Briefcase,
    name: 'Leadership tension',
    phase: 'Expansion',
    description: 'Leadership and manager dynamic intelligence to reduce conflict-driven execution drag.',
  },
];

const packaging = [
  { tier: 'Pilot', focus: 'Single cohort, baseline analytics, and outcome tracking', idealFor: 'First deployment and proof of value' },
  { tier: 'Growth', focus: 'Multi-cohort operations, controls, and integrations', idealFor: 'Regional or business-unit expansion' },
  { tier: 'Platform', focus: 'Partner API, advanced tracks, and white-label pathways', idealFor: 'Enterprise-wide and network-level deployment' },
];

export default function EnterpriseExpansion() {
  useSEO({
    title: 'Enterprise Expansion Programs | TENSION for Work',
    description:
      'See how TENSION expands beyond couples into friendship, family, and leadership tension programs with enterprise packaging.',
    canonical: '/enterprise/expansion',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    keywords: ['leadership tension', 'family relationship wellness', 'enterprise expansion programs'],
  });

  return (
    <WarmMarketingBackdrop>
      <EnterpriseSalesNav variant="light" />
      <div className="enterprise-public-shell mx-auto min-h-screen max-w-6xl space-y-6 px-4 py-10 md:py-14">
        <header className="enterprise-hero-shell">
          <div className="enterprise-hero-overlay" />
          <div className="enterprise-hero-content">
          <p className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-violet-700">
            Expansion roadmap
          </p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900 md:text-5xl">
            Expansion roadmap for multi-program enterprise growth
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 md:text-lg">
            TENSION expands beyond couples into additional relationship domains while retaining one unified analytics,
            governance, and enterprise operating model.
          </p>
          </div>
        </header>

        <section className="grid gap-4 md:grid-cols-3">
          {tracks.map((track) => (
            <Card key={track.name} className="enterprise-surface-card enterprise-lift-card p-6">
              <div className="enterprise-icon-chip">
                <track.icon className="h-7 w-7 text-violet-600" aria-hidden />
              </div>
              <p className="mt-3 text-xs font-bold uppercase tracking-wide text-violet-700">{track.phase}</p>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">{track.name}</h2>
              <p className="mt-2 text-sm text-slate-700">{track.description}</p>
            </Card>
          ))}
        </section>

        <section className="enterprise-surface-card rounded-2xl border-fuchsia-100 p-6 md:p-8">
          <h2 className="text-xl font-bold text-slate-900 md:text-2xl">Enterprise packaging matrix</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {packaging.map((pkg) => (
              <Card key={pkg.tier} className="enterprise-surface-card enterprise-lift-card border-violet-100/70 p-4 hover:shadow-lg">
                <p className="text-xs font-bold uppercase tracking-wide text-violet-700">{pkg.tier}</p>
                <h3 className="mt-1 font-semibold text-slate-900">{pkg.focus}</h3>
                <p className="mt-2 text-sm text-slate-700">{pkg.idealFor}</p>
              </Card>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/business-inquiries?inquiryType=enterprise">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-95">
                Explore expansion pricing
              </Button>
            </Link>
            <Link to="/enterprise/platform">
              <Button variant="outline" className="border-violet-200 bg-white text-violet-800">
                Platform capabilities
              </Button>
            </Link>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1 text-xs text-violet-700">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            Program tracks are stored as org-level configuration in the enterprise API.
          </div>
        </section>

        <EnterpriseProofPack compact />
      </div>
    </WarmMarketingBackdrop>
  );
}
